import { signalObject, type SignalObject } from "omi";
import debounce from "lodash-es/debounce";

type RetryConfig = {
  /**
   * Maximum number of retry attempts
   */
  maxAttempts: number;
  /**
   * Delay between retries in milliseconds
   */
  delayMs: number;
  /**
   * Optional multiplier for exponential backoff
   */
  backoffMultiplier?: number;
};

type SignalOptions<T> = {
  /**
   * The default value to initialize the signal with
   */
  default?: T;
  /**
   * The number of milliseconds to debounce the async function, if debounce functionality is required
   */
  debounceMs?: number;
  /**
   * Optional retry configuration for failed requests
   */
  retry?: RetryConfig;
};

type NetworkSignalValue<T> = {
  isLoading: boolean;
  error: unknown | undefined;
  data: T | undefined;
};

export type NetworkSignal<T> = {
  /**
   * The signal object that holds the state of the network signal
   */
  signal: SignalObject<NetworkSignalValue<T>>;
  /**
   * Executes the async function with optional debouncing
   * @param asyncFunc - The async function to execute
   * @returns Promise resolving to the async function result or undefined
   */
  run: (asyncFunc: () => Promise<T>, options?: { cacheKey?: string }) => Promise<T | undefined>;
  /**
   * Clears the cached data and resets the signal to initial state
   */
  clear: () => void;
};

// Global cache store for network signals
const queryCache = new Map<string, unknown>();

function networkSignalInitialState<T>(dataDefault?: SignalOptions<T>["default"]): NetworkSignalValue<T> {
  return {
    isLoading: false,
    error: undefined,
    data: dataDefault || undefined,
  };
}

export function networkSignal<T>(options?: SignalOptions<T>): NetworkSignal<T> {
  const initialState = networkSignalInitialState(options?.default);
  const signal = signalObject<NetworkSignalValue<T>>(initialState);
  const debouncedExecuteAsync = options?.debounceMs
    ? debounce((asyncFunc: () => Promise<T>) => executeAsync(asyncFunc), options.debounceMs)
    : null;
  let cacheKey: string | undefined;

  /**
   * Executes the async function with retry logic
   */
  async function executeWithRetry(asyncFunc: () => Promise<T>, attempt = 0): Promise<T | undefined> {
    try {
      signal.isLoading.value = true;
      const res = await asyncFunc();

      signal.data.value = res;
      signal.error.value = undefined;

      if (cacheKey) {
        queryCache.set(cacheKey, res);
      }

      return res;
    } catch (error) {
      if (options?.retry && attempt < options.retry.maxAttempts - 1) {
        const delay = options.retry.backoffMultiplier
          ? options.retry.delayMs * Math.pow(options.retry.backoffMultiplier, attempt)
          : options.retry.delayMs;

        await new Promise(resolve => setTimeout(resolve, delay));
        return await executeWithRetry(asyncFunc, attempt + 1);
      }

      // Only set error state after all retries are exhausted
      signal.error.value = error;
      throw error;
    }
  }

  /**
   * Core execution logic for handling async operations
   */
  async function executeAsync(asyncFunc: () => Promise<T>): Promise<T | undefined> {
    try {
      return await executeWithRetry(asyncFunc, 0);
    } catch (error) {
      // Error is already set in signal.error by executeWithRetry
      return undefined;
    } finally {
      signal.isLoading.value = false;
    }
  }

  /**
   * Executes the async function with optional debouncing
   * @param asyncFunc - The async function to execute
   * @param options - Run options like cacheKey to cache responses against
   * @returns Promise resolving to the async function result or undefined
   */
  async function run(asyncFunc: () => Promise<T>, options?: { cacheKey?: string }): Promise<T | undefined> {
    if (options?.cacheKey) {
      cacheKey = options.cacheKey;

      if (queryCache.has(options.cacheKey)) {
        const res = queryCache.get(options.cacheKey) as T;
        signal.data.value = res;
        return res;
      }
    }

    signal.isLoading.value = true;

    if (debouncedExecuteAsync) {
      return debouncedExecuteAsync(asyncFunc);
    }
    return executeAsync(asyncFunc);
  }

  /**
   * Clears the cached data and resets the signal to initial state
   */
  function clear(): void {
    if (cacheKey) {
      queryCache.delete(cacheKey);
    }
    signal.data.value = options?.default;
    signal.error.value = undefined;
    signal.isLoading.value = false;
  }

  return { signal, run, clear };
}
